import { Component, EventEmitter, Input, OnInit, Output, input } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ITableColumn } from 'src/app/models/tableColumns';
import { TableColumnTypeEnum, WidthType } from 'src/app/models/tableColumns/tableColumn.interface';
import { ITableRowViewModel } from 'src/app/models/viewModels';
import {
  CheckboxClicked,
  DeleteClicked,
  ExpandFilterResultClicked,
  OptionSelected,
  RowActionClicked,
  RowClicked,
  ToggleClicked,
} from '../table-row/table-row.component';
import { AuditLogEventApiResponse } from 'src/app/models/api/accountManagement.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { FormControl } from '@angular/forms';
import { MenuAction } from 'src/app/models/menuAction.model';
import { ISelectOption } from 'src/app/models/selectOptions';

export enum PaginationOperationEnum {
  next = 'next',
  prev = 'prev',
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {
  @Output() public rowActionOnClick = new EventEmitter<RowActionClicked>();
  @Output() public rowOnClick = new EventEmitter<RowClicked>();
  @Output() public toggleOnClick = new EventEmitter<ToggleClicked>();
  @Output() public checkboxOnClick = new EventEmitter<CheckboxClicked>();
  @Output() public expandFilterResultsOnClick = new EventEmitter<ExpandFilterResultClicked>();
  @Output() public deleteRowOnClick = new EventEmitter<DeleteClicked>();
  @Output() public rowOnChangeState = new EventEmitter<RowClicked>();
  @Output() public pageOnChange = new EventEmitter<PaginationOperationEnum>();
  @Output() public multiCheckOnClick = new EventEmitter<MenuAction>();
  @Output() public dropdownOnSelection = new EventEmitter<OptionSelected>();
  @Output() public detailsClick = new EventEmitter<AuditLogEventApiResponse>();

  @Input() public tableColumns: ITableColumn[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public tableRows: Observable<BehaviorSubject<any>[]>;
  @Input() public isScrollable = true;
  @Input() public pageInfo: ITablePageInfo;
  public noData = input<boolean>(false);
  public isLoading = input<boolean>(false);
  public sortedColumn = input<SortColumn | undefined>(undefined);
  // Output event to notify the parent about changes
  @Output() sortedColumnChange = new EventEmitter<SortColumn>();

  @Input() public withPagination = false;
  @Input() public withCheckbox = false;
  @Input() public withCheckboxAll = false;
  @Input() public withActions = false;
  @Input() public withActionsIcon = false;
  @Input() public withDeleteIcon = false;
  @Input() public withExpandFilters = true;
  @Input() public withCopyIcon = false;
  @Input() public checkedRowsIds: Set<string> = new Set();
  @Input() public allCheckedControl: FormControl<boolean | null> = new FormControl<boolean>(false);
  @Input() public expandFilterResultControl: FormControl<boolean | null> = new FormControl<boolean>(false);
  @Input() public checkedRowsSupportedActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);

  @Input() public dropDownOptions: ISelectOption[] = [];

  @Input() public tableNotFoundText = 'No results found';
  @Input() public tableNotFoundIcon = 'assets/images/common/no-results-found.svg';

  public WidthTypeEnum = WidthType;
  public colTypesEnum = TableColumnTypeEnum;
  public columnsTotalStaticPixels = 0;

  ngOnInit(): void {
    this.tableColumns.forEach((column) => {
      if (column.widthType == WidthType.Pixel) {
        this.columnsTotalStaticPixels += column.width;
      }
    });

    this.allCheckedControl.valueChanges.subscribe((value: boolean | null) => {
      if (value === null || !value) {
        this.expandFilterResultsOnChange(false);
      }
    });
  }

  public columnSortClicked(column: ITableColumn): void {
    let sortIsDesc = true;

    if (column.isSortable) {
      const currentSortColumn = this.sortedColumn();
      if (currentSortColumn && currentSortColumn.column.sortKey === column.sortKey) {
        sortIsDesc = !currentSortColumn.isDesc;
      }

      this.sortedColumnChange.emit({
        column: column,
        isDesc: sortIsDesc,
      } as SortColumn);
    }
  }

  public onRowClick(row: BehaviorSubject<ITableRowViewModel>): void {
    this.rowOnClick.emit({
      row: row,
    });
  }

  public onRowActionClick(event: RowActionClicked): void {
    this.rowActionOnClick.emit(event);
  }

  public onToggleClick(event: ToggleClicked): void {
    this.toggleOnClick.emit(event);
  }

  public onCheckboxClick(event: CheckboxClicked): void {
    this.checkboxOnClick.emit(event);
  }

  public onDeleteIconClick(item: DeleteClicked): void {
    this.deleteRowOnClick.emit(item);
  }

  public onRowChangeState(checked: RowClicked): void {
    this.rowOnChangeState.emit(checked);
  }

  public nextPageClicked(): void {
    this.pageOnChange.emit(PaginationOperationEnum.next);
  }

  public prevPageClicked(): void {
    this.pageOnChange.emit(PaginationOperationEnum.prev);
  }

  public onOptionSelected(event: OptionSelected): void {
    this.dropdownOnSelection.emit(event);
  }

  public onDetailsClick(event: AuditLogEventApiResponse): void {
    this.detailsClick.emit(event);
  }

  public expandFilterResultsOnChange(value?: boolean) {
    if (value == undefined) {
      this.expandFilterResultControl.setValue(!this.expandFilterResultControl.value);
    } else {
      this.expandFilterResultControl.setValue(value);
    }

    this.expandFilterResultsOnClick.emit({
      status: this.expandFilterResultControl.value ? this.expandFilterResultControl.value : false,
    });
  }

  public getTooltipText(row: BehaviorSubject<ITableRowViewModel>): string | null {
    const rowValue = row.value as ITableRowViewModel & { rowTooltipText?: string };
    if (rowValue && rowValue.rowTooltipText) {
      return rowValue.rowTooltipText;
    }
    return null;
  }
}
